import {
  WISHLIST_DESCRIPTION_MAX_LENGTH,
  WISHLIST_ITEM_PRICE_HINT_MAX_LENGTH,
  WISHLIST_ITEM_TEXT_MAX_LENGTH,
  WISHLIST_ITEM_TITLE_MAX_LENGTH,
  WISHLIST_ITEM_URL_MAX_LENGTH,
  WISHLIST_OCCASION_LABEL_MAX_LENGTH,
  WISHLIST_PRIVATE_NOTE_MAX_LENGTH,
  WISHLIST_TITLE_MAX_LENGTH,
  type CreateWishlistItemRequest,
  type CreateWishlistRequest,
} from '@nexus/api-types';
import type {
  WishlistCreateBeneficiaryOption,
  WishlistCreateDraft,
  WishlistCreateFieldErrors,
  WishlistCreateItemDraft,
  WishlistCreateItemPriority,
  WishlistCreateStep,
} from '@nexus/customer/wishlists/model';
import { WISHLIST_CREATE_OCCASION_LABELS } from './constants/wishlist-create-copy.constants';

export const EMPTY_WISHLIST_CREATE_FIELD_ERRORS: WishlistCreateFieldErrors = {
  title: null,
  description: null,
  customOccasionLabel: null,
  beneficiary: null,
  visibility: null,
  invitedEmail: null,
  itemTitle: null,
  itemDescription: null,
  itemUrl: null,
  itemPrice: null,
  itemPrivateNote: null,
};

export function validateWishlistCreateDraftStep(
  draft: WishlistCreateDraft,
  step: WishlistCreateStep,
): WishlistCreateFieldErrors {
  if (step === 'basic') {
    return validateWishlistCreateBasic(draft);
  }

  if (step === 'beneficiary') {
    return validateWishlistCreateBeneficiary(draft);
  }

  if (step === 'visibility') {
    return validateWishlistCreateVisibility(draft);
  }

  return EMPTY_WISHLIST_CREATE_FIELD_ERRORS;
}

export function validateWishlistCreateBasic(draft: WishlistCreateDraft): WishlistCreateFieldErrors {
  return {
    ...EMPTY_WISHLIST_CREATE_FIELD_ERRORS,
    title: draft.title.trim().length === 0 ? 'Podaj nazwę wishlisty.'
      : draft.title.trim().length > WISHLIST_TITLE_MAX_LENGTH ? `Nazwa może mieć maksymalnie ${WISHLIST_TITLE_MAX_LENGTH} znaków.` : null,
    description: draft.description.trim().length > WISHLIST_DESCRIPTION_MAX_LENGTH
      ? `Opis może mieć maksymalnie ${WISHLIST_DESCRIPTION_MAX_LENGTH} znaków.` : null,
    customOccasionLabel: draft.occasion === 'custom' && draft.customOccasionLabel.trim().length > WISHLIST_OCCASION_LABEL_MAX_LENGTH
      ? `Okazja może mieć maksymalnie ${WISHLIST_OCCASION_LABEL_MAX_LENGTH} znaków.` : null,
  };
}

export function validateWishlistCreateBeneficiary(draft: WishlistCreateDraft): WishlistCreateFieldErrors {
  return {
    ...EMPTY_WISHLIST_CREATE_FIELD_ERRORS,
    beneficiary: draft.beneficiaryOptionId === null ? 'Wybierz, dla kogo jest ta lista.' : null,
  };
}

export function validateWishlistCreateVisibility(draft: WishlistCreateDraft): WishlistCreateFieldErrors {
  return {
    ...EMPTY_WISHLIST_CREATE_FIELD_ERRORS,
    visibility: draft.visibility.length === 0 ? 'Wybierz widoczność wishlisty.' : null,
    invitedEmail:
      draft.visibility === 'invited_only' && draft.invitedEmails.some((email: string): boolean => !isWishlistEmailValid(email))
        ? 'Podaj poprawny adres email.'
        : null,
  };
}

export function validateWishlistCreateItemDraft(item: WishlistCreateItemDraft): WishlistCreateFieldErrors {
  return {
    ...EMPTY_WISHLIST_CREATE_FIELD_ERRORS,
    itemTitle: item.title.trim().length === 0 ? 'Podaj nazwę prezentu.'
      : item.title.trim().length > WISHLIST_ITEM_TITLE_MAX_LENGTH ? `Nazwa może mieć maksymalnie ${WISHLIST_ITEM_TITLE_MAX_LENGTH} znaków.` : null,
    itemDescription: item.description.trim().length > WISHLIST_ITEM_TEXT_MAX_LENGTH
      ? `Opis może mieć maksymalnie ${WISHLIST_ITEM_TEXT_MAX_LENGTH} znaków.` : null,
    itemUrl: item.storeUrl.trim().length > WISHLIST_ITEM_URL_MAX_LENGTH
      ? `Link może mieć maksymalnie ${WISHLIST_ITEM_URL_MAX_LENGTH} znaków.`
      : item.storeUrl.trim().length > 0 && !isWishlistUrlValid(item.storeUrl) ? 'Podaj poprawny link.' : null,
    itemPrice:
      (toPriceHint(item.priceAmount)?.length ?? 0) > WISHLIST_ITEM_PRICE_HINT_MAX_LENGTH
        ? `Cena może mieć maksymalnie ${WISHLIST_ITEM_PRICE_HINT_MAX_LENGTH} znaków.`
        : item.priceAmount.trim().length > 0 && !isWishlistPositivePrice(item.priceAmount)
        ? 'Cena musi być dodatnia.'
        : null,
    itemPrivateNote: item.privateNote.trim().length > WISHLIST_PRIVATE_NOTE_MAX_LENGTH
      ? `Notatka może mieć maksymalnie ${WISHLIST_PRIVATE_NOTE_MAX_LENGTH} znaków.` : null,
  };
}

export function hasWishlistCreateErrors(errors: WishlistCreateFieldErrors): boolean {
  return Object.values(errors).some((error: string | null): boolean => error !== null);
}

export function isWishlistEmailValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function isWishlistUrlValid(value: string): boolean {
  const normalizedValue: string = value.trim();
  if (normalizedValue.length === 0) {
    return true;
  }

  try {
    const parsedUrl: URL = new URL(normalizedValue);
    const hasPublicHostname: boolean = /^(?:[a-z0-9-]+\.)+(?:[a-z]{2,}|xn--[a-z0-9-]{2,})$/i.test(parsedUrl.hostname);
    const hasIpHostname: boolean = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(parsedUrl.hostname);
    const hasIpv6Hostname: boolean = parsedUrl.hostname.startsWith('[') && parsedUrl.hostname.endsWith(']');
    return (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') && (hasPublicHostname || hasIpHostname || hasIpv6Hostname);
  } catch {
    return false;
  }
}

export function isWishlistPositivePrice(value: string): boolean {
  const parsedValue = Number(value.replace(',', '.').trim());
  return Number.isFinite(parsedValue) && parsedValue > 0;
}

export function buildCreateWishlistRequest(
  draft: WishlistCreateDraft,
  beneficiary: WishlistCreateBeneficiaryOption,
): CreateWishlistRequest {
  return {
    title: draft.title.trim(),
    description: toNullableTrimmedText(draft.description),
    occasionLabel: toOccasionLabel(draft),
    occasionDate: toNullableTrimmedText(draft.occasionDate),
    beneficiaryKind: beneficiary.beneficiaryKind,
    beneficiaryProfileId: beneficiary.beneficiaryProfileId,
    visibility: draft.visibility,
    surpriseMode: false,
    items: draft.items.map(
      (item: WishlistCreateItemDraft, index: number): CreateWishlistItemRequest =>
        buildCreateWishlistItemRequest(item, index),
    ),
  };
}

export function buildCreateWishlistItemRequest(
  item: WishlistCreateItemDraft,
  index: number,
): CreateWishlistItemRequest {
  return {
    title: item.title.trim(),
    description: toNullableTrimmedText(item.description),
    url: toNullableTrimmedText(item.storeUrl),
    priceHint: toPriceHint(item.priceAmount),
    priority: toWishlistPriorityValue(item.priority),
    position: index,
    privateNote: toNullableTrimmedText(item.privateNote),
  };
}

export function toWishlistPriorityValue(priority: WishlistCreateItemPriority): number {
  if (priority === 'high') {
    return 80;
  }

  if (priority === 'medium') {
    return 50;
  }

  return 20;
}

function toOccasionLabel(draft: WishlistCreateDraft): string | null {
  if (draft.occasion === null) {
    return null;
  }

  if (draft.occasion === 'custom') {
    return toNullableTrimmedText(draft.customOccasionLabel);
  }

  return WISHLIST_CREATE_OCCASION_LABELS[draft.occasion];
}

function toPriceHint(value: string): string | null {
  const trimmedValue: string = value.trim();
  return trimmedValue.length > 0 ? `${trimmedValue.replace(',', '.')} PLN` : null;
}

function toNullableTrimmedText(value: string): string | null {
  const trimmedValue: string = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
}
